import { apiGet } from '../util/HttpApi';
import iconsRaw from '../resources/generated/poiicons.json';
import capitalize from 'lodash-es/capitalize';
import upperFirst from 'lodash-es/upperFirst';
import isEmpty from 'lodash-es/isEmpty';
import {
    CATEGORY_ICON,
    CATEGORY_KEY_NAME,
    CATEGORY_NAME,
    MAIN_CATEGORY_KEY_NAME,
    getSvgIcon,
    ICON_KEY_NAME,
    POI_ICON_NAME,
    TYPE_OSM_TAG,
    TYPE_OSM_VALUE,
    SEPARATOR,
    getOsmIdFromOsmUrl,
    OSM_WIKI,
    POI_SUBTYPE,
    POI_TYPE,
} from '../infoblock/components/wpt/WptTagsProvider';
import {
    changeIconColor,
    createPoiIcon,
    getIconUrlByName,
    POI_ICON_TYPE,
    removeShadowFromIconWpt,
} from '../map/markers/MarkerOptions';
import { CategoryIcon } from '../menu/configuremap/PoiCategoriesConfig';
import React from 'react';
import i18n from '../i18n';
import { getApiLang } from '../util/lang';
import SEARCH_ICON_BRAND_URL from '../assets/icons/ic_action_poi_brand.svg';
import { SEARCH_BRAND } from './searchConstants';
import { MAIN_URL_WITH_SLASH, MAP_OBJ_URL, POI_URL } from './GlobalManager';

const icons = new Set(iconsRaw);

const POI_CATEGORIES = 'poiCategories';
const TOP_POI_FILTERS = 'topPoiFilters';
const VALUE_LABEL_NAME = 'NAME';
const VALUE_LABEL_WHOLE = 'WHOLE';
export const DEFAULT_POI_ICON = 'craft_default';
export const DEFAULT_POI_COLOR = '#f8931d';
export const DEFAULT_ICON_COLOR = '#ffffff';
export const DEFAULT_POI_SHAPE = 'circle';

const poiFilters = {
    accomodation: ['Accomodation'],
    cafe_and_restaurant: ['Cafe and restaurant'],
    charging_station: ['Charging station'],
    emergency: ['Emergency'],
    entertainment: ['Entertainment', 'Leisure'],
    filling_station: ['Filling station'],
    finance: ['Finance'],
    healthcare: ['Healthcare'],
    parking: ['Parking'],
    personal_transport: ['Personal transport'],
    public_transport: ['Public transport'],
    routes: ['Routes'],
    seamark: ['Seamark', 'Nautical'],
    shop: ['Shop'],
    shop_food: ['Shop food', 'Convenience store and supermarket'],
    sightseeing: ['Sightseeing'],
    sport: ['Sport'],
    sustenance: ['Sustenance', 'Food'],
    tourism: ['Tourism'],
    water_filter: ['Water filter', 'Water'],
};
export const poiUrlParams = {
    name: 'name',
    type: 'type',
    osmId: 'osmId',
    wikidataId: 'wikidataId',
    pin: 'pin',
};

async function getPoiCategories() {
    let categories = JSON.parse(localStorage.getItem(POI_CATEGORIES));
    if (
        categories &&
        typeof categories === 'object' &&
        Object.keys(categories).length > 0 &&
        categories.entertainment?.length > 0
    ) {
        return categories; // thoroughly verified categories from localStorage
    } else {
        let response = await apiGet(`${process.env.REACT_APP_ROUTING_API_SITE}/search/get-poi-categories`, {
            apiCache: true,
            params: {
                locale: getApiLang(i18n.language),
            },
        });
        if (response.data) {
            localStorage.setItem(POI_CATEGORIES, JSON.stringify(response.data));
            return response.data;
        }
    }
}

async function getTopPoiFilters() {
    let filters = JSON.parse(localStorage.getItem(TOP_POI_FILTERS));
    if (filters?.length > 0) {
        return filters;
    } else {
        let response = await apiGet(`${process.env.REACT_APP_ROUTING_API_SITE}/search/get-top-filters`);
        if (response.data) {
            localStorage.setItem(TOP_POI_FILTERS, JSON.stringify(response.data));
            return response.data;
        }
    }
}

async function searchPoiCategories(search, center) {
    if (!center) {
        return null;
    }
    let response = await apiGet(`${process.env.REACT_APP_ROUTING_API_SITE}/search/search-poi-categories`, {
        apiCache: true,
        params: {
            search: search,
            lat: center.lat,
            lon: center.lng,
            locale: getApiLang(i18n.language),
        },
    });
    if (!isEmpty(response?.data)) {
        return response.data;
    } else {
        return null;
    }
}

export function getIconNameForPoiType({
    iconKeyName,
    typeOsmTag = '',
    typeOsmValue = '',
    iconName = '',
    useDefault = true,
}) {
    if (icons.has(`mx_${typeOsmTag}_${typeOsmValue}.svg`)) {
        return `${typeOsmTag}_${typeOsmValue}`;
    } else if (icons.has(`mx_${iconKeyName}.svg`)) {
        return iconKeyName;
    } else if (icons.has(`mx_topo_${iconKeyName}.svg`)) {
        return `topo_${iconKeyName}`;
    } else if (iconName !== 'null' && icons.has(`mx_${iconName}.svg`)) {
        return iconName;
    } else {
        return useDefault ? DEFAULT_POI_ICON : null;
    }
}

// icon of a POI feature from the server (web_* props)
export function getFinalPoiIconName(properties) {
    return getIconNameForPoiType({
        iconKeyName: properties[ICON_KEY_NAME],
        typeOsmTag: properties[TYPE_OSM_TAG],
        typeOsmValue: properties[TYPE_OSM_VALUE],
        iconName: properties[POI_ICON_NAME],
    });
}

export function getCatPoiIconName(props) {
    let iconName = getIconNameForPoiType({
        iconKeyName: props[ICON_KEY_NAME],
        typeOsmTag: props[TYPE_OSM_TAG],
        typeOsmValue: props[TYPE_OSM_VALUE],
        iconName: props[POI_ICON_NAME],
        useDefault: false,
    });
    if (!iconName) {
        // get from parent category
        iconName = getIconNameForPoiType({
            iconKeyName: props[MAIN_CATEGORY_KEY_NAME],
            iconName: props[CATEGORY_ICON],
        });
    }
    return iconName;
}

export function getIconName(obj) {
    if (icons.has(`mx_${obj.key}.svg`)) {
        return obj.key;
    } else if (icons.has(`mx_${obj.value}.svg`)) {
        return obj.value;
    }
    return null;
}

function formattingPoiFilter(type, rename) {
    if (type && poiFilters[type]) {
        if (rename) {
            type = poiFilters[type].length > 1 ? poiFilters[type][1] : poiFilters[type][0];
        } else {
            type = poiFilters[type][0];
        }
    }
    return type;
}

// the first value of a list the server sends as "value1;value2"
export function getFirstSubstring(inputString) {
    if (inputString?.includes(SEPARATOR)) {
        return inputString.split(SEPARATOR)[0];
    }

    return inputString;
}

// tag of a poi type with the language it is written in: "brand:de"
export function parseTagWithLang(tag) {
    if (typeof tag !== 'string' || !tag.includes(':')) {
        return { key: tag, lang: null };
    }
    const [key, lang] = tag.split(':');

    return { key, lang };
}

export function hasTypeTranslation(type) {
    return i18n.exists(`amenity_type_${type}`) || i18n.exists(`poi_${type}`);
}

// a map object without a poi type (building=yes, ...): the type is the tag that has a translation, as in Android
export function findTranslatedTag(tags) {
    for (const [key, value] of Object.entries(tags ?? {})) {
        const tag = key.replaceAll(':', '_');
        const translated = [`${tag}_${value}`, value, tag].find(
            (candidate) => candidate && hasTypeTranslation(candidate)
        );
        if (translated) {
            return translated;
        }
    }
    return null;
}

// name of a poi type as it is shown to the user, optionally in another language
export function preparedType(type, t, lang = null) {
    let restoreLang;
    if (t && lang) {
        restoreLang = i18n.language;
        i18n.changeLanguage(lang);
    }

    const res = capitalize(t(`amenity_type_${type}`, formattingPoiType(t(`poi_${type}`))));

    if (restoreLang) {
        i18n.changeLanguage(restoreLang);
    }

    return res;
}

export function formattingPoiType(type) {
    if (type) {
        type = type.replaceAll('_', ' ');
        type = type.charAt(0).toUpperCase() + type.slice(1);
    }
    return type;
}

function preparePoiFilterIcon(filter) {
    if (filter === 'water_filter') {
        return 'amenity_drinking_water';
    }
    return filter;
}

// Dedupes SVG fetches by URL (including in-flight) so a batch of icon loads hits each file once,
// which the React-state cache can't do within one Promise.all (avoids ERR_INSUFFICIENT_RESOURCES).
const svgIconRequests = new Map();

async function fetchSvgIcon(url) {
    if (svgIconRequests.has(url)) {
        return svgIconRequests.get(url);
    }
    const request = fetch(url)
        .then((response) => {
            if (response.ok) {
                return response.text();
            }
            if (response.status >= 500 || response.status === 429) {
                svgIconRequests.delete(url);
            }
            console.warn(`Search icon not found (${response.status}): ${url}`);

            return null;
        })
        .catch((error) => {
            console.error(`Failed to fetch SVG icon from ${url}: ${error}`);
            svgIconRequests.delete(url);

            return null;
        });
    svgIconRequests.set(url, request);

    return request;
}

/**
 * Asynchronously creates a cache of Point of Interest (POI) icons.
 *
 * @param {Array} poiList - The list of POIs for which icons should be cached.
 * @param {Object} obj - An optional object representing a single POI.
 * @param {Object} poiIconCache - The existing cache of POI icons.
 * @param {String} icon - An optional icon name.
 * @returns {Promise<Object>} - The updated cache of POI icons.
 */
export async function createPoiCache({ poiList = null, obj = null, poiIconCache, icon = null }) {
    const iconCache = {};
    const missingIcons = new Set();
    const arr = icon ? [icon] : (poiList ?? [obj]);
    for (const poi of arr) {
        if (!poi) {
            continue;
        }
        let iconWpt;
        if (icon) {
            iconWpt = icon;
        } else if (obj) {
            iconWpt = getIconName(obj);
        } else {
            iconWpt = getFinalPoiIconName(poi.properties);
        }
        if (iconWpt) {
            // If the icon is already in the existing cache, copy it to the updated cache
            if (poiIconCache[iconWpt]) {
                iconCache[iconWpt] = poiIconCache[iconWpt];
            } else {
                missingIcons.add(iconWpt);
            }
        }
    }
    await Promise.all(
        [...missingIcons].map(async (name) => {
            const svgData = await fetchSvgIcon(getIconUrlByName(POI_ICON_TYPE, name));
            if (svgData) {
                iconCache[name] = svgData;
            }
        })
    );

    return iconCache;
}

export function updatePoiCache(ctx, newData) {
    ctx.poiIconCache = {
        ...ctx.poiIconCache,
        ...newData,
    };
    ctx.setPoiIconCache({ ...ctx.poiIconCache });
}

export const cleanHtml = (html) => {
    return html
        .replace('div class="content"', '')
        .replace(/[ \u00a0]*\([^)]*\)/g, '')
        .replace(/[ \u00a0]*\[[^\]]*\]/g, '')
        .replace(/[})]/g, '')
        .replace(/http\S*\s/g, '');
};

export function translatePoi({ key = null, value = null, ctx, t }) {
    if (key === null && value === null) {
        return '';
    }
    if (key !== null && ctx.poiCategory?.filters !== null) {
        return t('poi_' + ctx.poiCategory?.filters[key]);
    } else if (value !== null) {
        return t('poi_' + value);
    }
    return '';
}

// obf top-index keys ("top_index_brand_okko") are agreed with the server: SpatialPoiSearch.byKey format
export const TOP_INDEX_PREFIX = 'top_index_';

export function getTopIndexValueName(key) {
    const rest = key.substring(TOP_INDEX_PREFIX.length);
    const valueStart = rest.indexOf('_');

    return valueStart === -1 ? rest : rest.substring(valueStart + 1);
}

/**
 * Check if type is a brand (contains ':' separator)
 * Brand format: brandName:lang
 */
export function isBrandType(type) {
    return (
        type &&
        type.includes(':') &&
        !type.includes('name:') &&
        !type.includes('lang:') &&
        !type.startsWith(TOP_INDEX_PREFIX)
    );
}

/**
 * Parse brand type to extract brand name and language
 * @returns {Object} { brandName, lang } or null if not a brand
 */
export function parseBrandType(type) {
    if (!isBrandType(type)) {
        return null;
    }
    const [brandName, lang] = type.split(':');
    return { brandName, lang };
}

/**
 * Get category name from category type and translation function
 * Handles special cases: name:lang and lang:lang formats
 */
export function getCategoryName(category, t) {
    if (category?.startsWith(TOP_INDEX_PREFIX)) {
        return getTopIndexValueName(category);
    }
    if (!category) return '';
    if (category.includes('name:')) {
        const [mainPart, subPart] = category.split(':');
        return getFirstSubstring(t(`poi_${mainPart}`)) + ' (' + t(`lang_${subPart}`) + ')';
    } else if (category.includes('lang:')) {
        const preparedCategory = category.replace(':', '_');
        return getFirstSubstring(t(`poi_${preparedCategory}`));
    }
    return getFirstSubstring(t(`poi_${category}`));
}

export function getCategoryItemName(item, t) {
    return item[CATEGORY_NAME] ?? getCategoryName(item[CATEGORY_KEY_NAME], t);
}

export async function getCategoryIcon(category) {
    const name = PoiManager.preparePoiFilterIcon(category);
    return (
        <CategoryIcon
            color={DEFAULT_POI_COLOR}
            background={DEFAULT_POI_SHAPE}
            icon={name}
            iconSize={20}
            shieldSize={30}
        />
    );
}

export async function getSearchResultIcon({ result, ctx, isCategory = false, iconUrl = null }) {
    const name = isCategory ? PoiManager.preparePoiFilterIcon(result) : result;
    if (result === SEARCH_BRAND) {
        iconUrl = SEARCH_ICON_BRAND_URL;
    }
    let svgData;
    if (iconUrl) {
        if (ctx.poiIconCache?.[name]) {
            svgData = ctx.poiIconCache[name];
        } else {
            svgData = await fetchSvgIcon(iconUrl);
            if (svgData) {
                ctx.setPoiIconCache((prevState) => ({
                    ...prevState,
                    [name]: svgData,
                }));
            }
        }
    } else {
        svgData = await getSvgIcon({ ctx, icon: name });
    }
    const coloredSvg = changeIconColor(svgData, DEFAULT_POI_COLOR);
    const iconHtml = createPoiIcon({
        color: '#F0F0F0',
        background: DEFAULT_POI_SHAPE,
        hasBackgroundLight: false,
        svgIcon: coloredSvg,
        iconSize: 24,
        backgroundSize: 48,
    }).options.html;

    return (
        <div
            style={{ display: 'flex' }}
            dangerouslySetInnerHTML={{
                __html: removeShadowFromIconWpt(iconHtml) + '',
            }}
        />
    );
}

export function splitNameValue(translation) {
    const index = translation?.indexOf(':') ?? -1;
    const name = translation?.slice(0, index).trim();
    const value = translation?.slice(index + 1).trim();

    return index > 0 && /\p{L}/u.test(name) && value ? [name, value] : null;
}

export function getValueLabelParts(translation, label) {
    const nameValue = label && label !== VALUE_LABEL_WHOLE ? splitNameValue(translation) : null;
    if (!nameValue) {
        return { tagName: null, tagValue: translation };
    }

    return label === VALUE_LABEL_NAME
        ? { tagName: null, tagValue: nameValue[0] }
        : { tagName: nameValue[0], tagValue: upperFirst(nameValue[1]) };
}

export function translateWithSplit(t, string) {
    let translatedString = t(string);
    if (translatedString?.includes(SEPARATOR)) {
        translatedString = translatedString.split(SEPARATOR)[0];
    }
    return translatedString;
}

export function navigateToPoi(obj, navigate, isWiki = false) {
    if (!obj) return;

    const params = {};

    const wiki = obj.wikidata;
    const poi = obj.poi;

    if (isWiki) {
        Object.assign(params, getWikiPoiParams(poi, wiki));
    } else {
        Object.assign(params, getPoiParams(poi));
    }

    const keys = Object.keys(poiUrlParams);
    const search = new URLSearchParams();
    for (const key of keys) {
        const v = params[key];
        if (v != null && v !== '') {
            search.append(key, v);
        }
    }
    navigate({
        pathname: MAIN_URL_WITH_SLASH + POI_URL,
        search: search.size ? `?${search}` : '',
        hash: globalThis.location.hash,
    });
}

function getWikiPoiParams(poi, wiki) {
    const params = {};
    // from wiki properties
    params.type = getWikiPoiType(wiki.properties);
    params.pin = getPinParam(wiki.geometry.coordinates?.[1], wiki.geometry.coordinates?.[0]);
    // from poi properties
    params.name = poi?.properties.web_poi_name;
    params.osmId = params.name ? null : getOsmIdFromOsmUrl(poi?.properties.web_poi_osmUrl);

    const wikidataIdFromOsm = poi?.properties.osm_tag_wikidata;
    params.wikidataId = wikidataIdFromOsm ? null : wiki.properties?.id;

    return params;
}

function getPoiParams(poi) {
    const params = {};
    params.type = getFirstSubstring(poi.options[POI_SUBTYPE] ?? poi.options[POI_TYPE]);
    params.pin = getPinParam(poi.latlng?.lat, poi.latlng?.lng);
    params.name = poi.options.amenity_name || poi.options.name;
    params.osmId = params.name ? null : getOsmIdFromOsmUrl(poi.options.web_poi_osmUrl);

    return params;
}

function getPinParam(lat, lon) {
    return Number.isFinite(lat) && Number.isFinite(lon) ? `${lat.toFixed(6)},${lon.toFixed(6)}` : null;
}

// an object outside the poi index cannot be reopened by its url, so the url keeps only the pin
export function navigateToPin(latlng, navigate) {
    const pin = getPinParam(latlng?.lat, latlng?.lng);
    navigate({
        pathname: MAIN_URL_WITH_SLASH + MAP_OBJ_URL,
        search: pin ? `?${new URLSearchParams({ pin })}` : '',
        hash: globalThis.location.hash,
    });
}

function getWikiPoiType(props) {
    const type = props.poitype ?? props.subtype;
    return type ? preparedType(type, i18n?.t, 'en') : OSM_WIKI;
}

const PoiManager = {
    getPoiCategories,
    searchPoiCategories,
    getFinalPoiIconName,
    getTopPoiFilters,
    formattingPoiType,
    formattingPoiFilter,
    preparePoiFilterIcon,
    poiFilters,
};

export default PoiManager;
